import React, { useState } from "react";
import type { ChromeStoreReview } from "@/hooks/useChromeWebStore";

type ChromeStoreTestimonialsProps = {
  reviews: ChromeStoreReview[];
  loading: boolean;
  storeUrl: string;
};

const Stars: React.FC<{ rating: number }> = ({ rating }) => (
  <span className="text-white/70 tracking-[0.2em] text-xs" aria-label={`${rating} out of 5 stars`}>
    {"★".repeat(Math.max(0, Math.min(5, Math.round(rating))))}
    <span className="text-white/20">
      {"★".repeat(Math.max(0, 5 - Math.round(rating)))}
    </span>
  </span>
);

const Avatar: React.FC<{ review: ChromeStoreReview }> = ({ review }) => {
  const [failed, setFailed] = useState(false);
  const initial = review.author.trim().charAt(0).toUpperCase() || "?";

  if (!review.avatar || failed) {
    return (
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-white/15 text-[11px] uppercase text-white/70">
        {initial}
      </span>
    );
  }

  return (
    <img
      src={review.avatar}
      alt=""
      className="h-9 w-9 shrink-0 rounded-full object-cover border border-white/15"
      onError={() => setFailed(true)}
    />
  );
};

const ChromeStoreTestimonials: React.FC<ChromeStoreTestimonialsProps> = ({
  reviews,
  loading,
  storeUrl,
}) => {
  if (loading && reviews.length === 0) {
    return (
      <section className="px-6 md:px-12 lg:px-24 mb-20">
        <h2
          className="uppercase text-[#e4e4e4] mb-8 leading-none"
          style={{
            fontFamily: "'League Gothic', sans-serif",
            fontSize: "clamp(2rem, 5vw, 3.5rem)",
          }}
        >
          Testimonials
        </h2>
        <div className="grid gap-4 md:grid-cols-2">
          {Array.from({ length: 4 }).map((_, index) => (
            <div
              key={index}
              className="border border-white/10 px-5 py-6 min-h-[160px] animate-pulse bg-white/[0.03]"
            />
          ))}
        </div>
      </section>
    );
  }

  if (!reviews.length) return null;

  return (
    <section className="px-6 md:px-12 lg:px-24 mb-20">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <h2
          className="uppercase text-[#e4e4e4] leading-none"
          style={{
            fontFamily: "'League Gothic', sans-serif",
            fontSize: "clamp(2rem, 5vw, 3.5rem)",
          }}
        >
          Testimonials
        </h2>
        <p className="text-white/30 text-[10px] uppercase tracking-[0.25em]">
          Live from Chrome Web Store
        </p>
      </div>
      <div className="grid gap-4 md:grid-cols-2">
        {reviews.map((review) => (
          <article
            key={review.id}
            className="border border-white/10 px-5 py-6 flex flex-col gap-4"
          >
            <Stars rating={review.rating} />
            <p className="text-[#cfcfcf] text-sm md:text-base leading-relaxed whitespace-pre-line">
              “{review.text}”
            </p>
            <div className="mt-auto flex items-center gap-3 pt-2">
              <Avatar review={review} />
              <div>
                <p className="text-[#e4e4e4] text-sm font-medium">{review.author}</p>
                {review.date && (
                  <p className="text-white/35 text-[10px] uppercase tracking-[0.18em] mt-1">
                    {review.date}
                  </p>
                )}
              </div>
            </div>
          </article>
        ))}
      </div>
      <a
        href={storeUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-block mt-8 text-[11px] uppercase tracking-[0.2em] text-white/45 hover:text-white transition-colors"
      >
        See all reviews on Chrome Web Store →
      </a>
    </section>
  );
};

export default ChromeStoreTestimonials;
